import { useEffect, useMemo, useState } from 'react';
import { api, type HandsOnMark, type TestCaseInfo, type TestCenterView, type TestResultView, type TestRunView } from '../../api/client';
import { Button, Card, Chip, Dialog, Empty, Kpi, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { shortWhen, type Tone } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

type Shown = TestResultView['status'] | 'waiting' | 'running';

const AUTO_STATUS: Record<Shown, { label: string; tone: Tone }> = {
  pass: { label: 'Passed', tone: 'ok' },
  warn: { label: 'Look at this', tone: 'warn' },
  fail: { label: 'Failed', tone: 'crit' },
  error: { label: 'Broke', tone: 'crit' },
  skip: { label: 'Skipped', tone: 'neutral' },
  waiting: { label: 'Waiting', tone: 'neutral' },
  running: { label: 'Running', tone: 'brand' }
};

const HANDS_STATUS: Record<HandsOnMark['status'], { label: string; tone: Tone }> = {
  pass: { label: 'Worked', tone: 'ok' },
  fail: { label: 'Didn’t work', tone: 'crit' },
  blocked: { label: 'Couldn’t try', tone: 'warn' }
};

const problem = (s: Shown) => s === 'warn' || s === 'fail' || s === 'error';

/** Cases in catalog order, grouped by area. */
function byArea<T extends { area: string }>(list: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const c of list) groups.set(c.area, [...(groups.get(c.area) ?? []), c]);
  return [...groups];
}

/**
 * Stitch Admin → Test center: runs a check of every part of Stitch, lists the hands-on checks to do
 * by hand, and puts it all in one JSON file to hand to whoever is fixing things.
 */
export function AdminTests() {
  const data = useData(() => api.testCenter(), []);
  const [chosen, setChosen] = useState<string | null>(null);
  const [run, setRun] = useState<TestRunView | null>(null);
  const [runError, setRunError] = useState<Error | null>(null);
  const [toast, setToast] = useState('');
  const v = data.data;
  const runId = chosen ?? v?.running ?? v?.runs[0]?.id ?? null;

  // Load the run shown; while it's going, look again every second and a half.
  const { reload } = data;
  useEffect(() => {
    if (!runId) return setRun(null);
    let live = true;
    let timer: number | undefined;
    const load = async () => {
      try {
        const r = (await api.testRun(runId)).run;
        if (!live) return;
        setRun(r);
        setRunError(null);
        if (!r.finishedAt) timer = window.setTimeout(load, 1500);
        else reload();
      } catch (err) {
        if (live) setRunError(err instanceof Error ? err : new Error(String(err)));
      }
    };
    void load();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [runId, reload]);

  const shown = run && run.id === runId ? run : null;
  const auto = v?.cases.filter((c) => c.kind === 'auto') ?? [];
  const manual = v?.cases.filter((c) => c.kind === 'manual') ?? [];
  const done = shown?.results.length ?? 0;
  const going = Boolean(shown && !shown.finishedAt);

  return (
    <>
      <PageHeader
        eyebrow={going ? `Running · ${done} of ${auto.length}` : 'Stitch Admin'}
        title="Test center"
        actions={
          shown && (
            <a className={`btn ${going ? 'secondary' : 'primary'}`} href={api.testReportUrl(shown.id)} download>
              <Icon name="download" size={17} strokeWidth={2.2} />
              Download JSON
            </a>
          )
        }
      />
      <div className="content">
        {data.error ? (
          <Card>
            <LoadError error={data.error} retry={data.reload} />
          </Card>
        ) : !v ? (
          <Loading label="Loading the test center" />
        ) : (
          <>
            <RunCard
              v={v}
              busy={going || Boolean(v.running)}
              onStarted={(r) => {
                setRun(r);
                setChosen(r.id);
                data.reload();
              }}
            />
            {runError && <Notice tone="crit">{runError.message}</Notice>}
            {!shown ? (
              runId ? (
                <Loading label="Loading the run" />
              ) : (
                <Card>
                  <Empty title="No runs yet">Run the checks above. Each run keeps its results and the hands-on checks you mark, ready to download.</Empty>
                </Card>
              )
            ) : (
              <>
                <Summary run={shown} autoTotal={auto.length} manualTotal={manual.length} />
                <AutoResults cases={auto} run={shown} />
                <HandsOn cases={manual} run={shown} onSaved={(r, msg) => (setRun(r), setToast(msg))} />
              </>
            )}
            <PastRuns v={v} current={runId} onOpen={setChosen} />
          </>
        )}
      </div>
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
    </>
  );
}

function RunCard({ v, busy, onStarted }: { v: TestCenterView; busy: boolean; onStarted: (r: TestRunView) => void }) {
  const [tenant, setTenant] = useState<string>(v.defaultTenant ?? '');
  const [writes, setWrites] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const chosen = v.tenants.find((t) => t.domain === tenant);
  const writable = chosen?.writable ?? false;
  // Only a tenant named in PBX_WRITE_TENANTS is a test tenant; anything else Stitch may change (a
  // customer's, 1.12.33, or any by `*`) may be real, so the changes check asks first, naming the company (2981).
  const real = writable && !chosen?.test;
  // The preferred tenant may not be on the phone system's list (a customer not set up yet).
  const options = v.defaultTenant && !v.tenants.some((t) => t.domain === v.defaultTenant) ? [{ domain: v.defaultTenant, name: '', writable: false, test: false, customer: null }, ...v.tenants] : v.tenants;
  const start = async (confirm?: string) => {
    setStarting(true);
    setError('');
    try {
      onStarted((await api.startTestRun({ tenant: tenant || null, writes: writes && writable, ...(confirm ? { confirm } : {}) })).run);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card title="Run the checks">
      <div className="card-body section-stack">
        <p className="muted tests-intro">
          Checks the phone system, the portal screens, sign-in, numbers, texting, storage, email and Leland, recording each outside call as its shape (never names, numbers or keys). Nothing here
          spends money, sends a text or changes a 911 address.
        </p>
        <div className="tests-run-row">
          <div className="field">
            <label htmlFor="tests-tenant">Tenant to look at</label>
            <select
              id="tests-tenant"
              className="input"
              value={tenant}
              onChange={(e) => {
                setTenant(e.target.value);
                setWrites(false);
              }}
            >
              <option value="">None (system checks only)</option>
              {options.map((t) => (
                <option key={t.domain} value={t.domain}>
                  {t.name ? `${t.name} · ${t.domain}` : t.domain}
                  {t.customer ? ` (customer: ${t.customer})` : t.test ? ' (test tenant)' : ''}
                </option>
              ))}
            </select>
          </div>
          <label className={`check-row tests-writes${writable ? '' : ' off'}`}>
            <input type="checkbox" checked={writes && writable} disabled={!writable} onChange={(e) => setWrites(e.target.checked)} />
            <span className="cell-stack">
              <span>Include changes on this tenant</span>
              <small className="muted">
                {!writable
                  ? 'Only on a tenant Stitch may change: a customer’s, or a test tenant named in PBX_WRITE_TENANTS.'
                  : real
                    ? `Not a test tenant${chosen?.customer ? `: this is ${chosen.customer}’s phone system` : ''}. Adds a person between 9900 and 9990 there, renames them, undoes it and removes them again. You’ll be asked first.`
                    : 'Adds a person between 9900 and 9990, renames them, undoes it and removes them again.'}
              </small>
            </span>
          </label>
          <Button
            variant="primary"
            icon="play"
            disabled={busy || starting}
            onClick={() => (writes && real ? setConfirming(true) : void start())}
          >
            {busy ? 'Running…' : starting ? 'Starting…' : 'Run all checks'}
          </Button>
        </div>
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
      {confirming && chosen && (
        <Dialog
          open
          onClose={() => setConfirming(false)}
          title="Make changes on a customer’s phone system?"
          subtitle={chosen.name ? `${chosen.name} · ${chosen.domain}` : chosen.domain}
          foot={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button variant="danger" disabled={starting} onClick={() => void start(chosen.domain)}>
                {starting ? 'Starting…' : `Run with changes on ${chosen.domain}`}
              </Button>
            </>
          }
        >
          <p>
            {chosen.domain} isn’t a test tenant{chosen.customer ? `: it’s ${chosen.customer}’s phone system` : ''}. Only tenants named in PBX_WRITE_TENANTS that no customer is linked to are test tenants.
          </p>
          <p className="muted">The check adds a person between 9900 and 9990 there, renames them, undoes it and removes them again. Nobody else’s settings, phones or calls change.</p>
        </Dialog>
      )}
    </Card>
  );
}

function Summary({ run, autoTotal, manualTotal }: { run: TestRunView; autoTotal: number; manualTotal: number }) {
  const count = (...s: string[]) => run.results.filter((r) => s.includes(r.status)).length;
  const marked = Object.keys(run.manual).length;
  const handsFail = Object.values(run.manual).filter((m) => m.status === 'fail').length;
  const going = !run.finishedAt;
  return (
    <>
      {going && (
        <div className="tests-progress" role="status">
          <progress max={autoTotal} value={run.results.length} aria-label="Checks done" />
          <span className="muted">
            {run.results.length} of {autoTotal} checks done
          </span>
        </div>
      )}
      <div className="kpis tests-kpis">
        <Kpi small label="Passed" value={count('pass')} foot={`of ${autoTotal} checks`} />
        <Kpi small label="Look at these" value={count('warn')} foot="Worked, with something odd" />
        <Kpi small label="Failed" value={count('fail', 'error')} foot={count('error') ? `${count('error')} broke while checking` : 'Did the wrong thing'} />
        <Kpi small label="Skipped" value={count('skip')} foot="Not set up, or not asked for" />
        <Kpi small label="Hands-on" value={`${marked} of ${manualTotal}`} foot={handsFail ? `${handsFail} didn’t work` : 'Marked so far'} />
      </div>
      <p className="muted small-print">
        Run by {run.startedByName} {shortWhen(run.startedAt)}
        {run.options.writes ? ' · with changes on the test tenant' : ' · reads only'}
        {run.options.version ? ` · on ${run.options.version}${run.options.build ? ` (${run.options.build.split(' ')[0]})` : ''}` : ''}
        {run.options.container ? `, container ${run.options.container}` : ''}
        {run.finishedAt ? '' : ' · still going'}
      </p>
    </>
  );
}

function AutoResults({ cases, run }: { cases: TestCaseInfo[]; run: TestRunView }) {
  const [filter, setFilter] = useState<'all' | 'problems'>('all');
  const results = useMemo(() => new Map(run.results.map((r) => [r.id, r])), [run.results]);
  const firstWaiting = cases.find((c) => !results.has(c.id))?.id;
  const statusOf = (c: TestCaseInfo): Shown => results.get(c.id)?.status ?? (run.finishedAt ? 'waiting' : c.id === firstWaiting ? 'running' : 'waiting');
  const shown = filter === 'all' ? cases : cases.filter((c) => problem(statusOf(c)));
  return (
    <Card
      title="Automatic checks"
      action={
        <div className="segmented small" role="group" aria-label="Show">
          {(['all', 'problems'] as const).map((f) => (
            <button key={f} type="button" className={filter === f ? 'on' : ''} aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : 'Problems only'}
            </button>
          ))}
        </div>
      }
    >
      {shown.length === 0 ? (
        <Empty title="Nothing to look at">{run.finishedAt ? 'Every check passed or was skipped.' : 'No problems so far.'}</Empty>
      ) : (
        byArea(shown).map(([area, list]) => (
          <section key={area} className="tests-area">
            <h3 className="tests-area-title">{area}</h3>
            <ul className="tests-list">
              {list.map((c) => (
                <AutoRow key={c.id} c={c} status={statusOf(c)} r={results.get(c.id)} />
              ))}
            </ul>
          </section>
        ))
      )}
    </Card>
  );
}

function AutoRow({ c, status, r }: { c: TestCaseInfo; status: Shown; r: TestResultView | undefined }) {
  const [open, setOpen] = useState(false);
  const s = AUTO_STATUS[status];
  const hasMore = Boolean(r && (r.facts || r.error || r.calls?.length));
  return (
    <li className={`tests-row ${status}`} data-case={c.id}>
      <div className="tests-row-head">
        <Chip small tone={s.tone}>
          {s.label}
        </Chip>
        <span className="cell-stack grow">
          <span className="tests-title">{c.title}</span>
          {r?.detail ? <small className={problem(status) ? 'tests-detail problem' : 'muted tests-detail'}>{r.detail}</small> : <small className="muted">{c.expect}</small>}
        </span>
        {r && r.status !== 'skip' && <span className="muted small-print tests-ms">{r.ms < 1000 ? `${r.ms} ms` : `${(r.ms / 1000).toFixed(1)} s`}</span>}
        {hasMore && (
          <button type="button" className="link-btn small-print" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide' : 'Details'}
          </button>
        )}
      </div>
      {open && r && (
        <div className="tests-more">
          <p className="small-print">
            <strong>Should:</strong> {c.expect}
          </p>
          {r.error && (
            <pre className="sip-text">
              {r.error.name}
              {r.error.code ? ` (${r.error.code})` : ''}: {r.error.message}
              {r.error.stack ? `\n${r.error.stack}` : ''}
            </pre>
          )}
          {r.facts && <pre className="sip-text">{JSON.stringify(r.facts, null, 2)}</pre>}
          {r.calls && r.calls.length > 0 && (
            <div className="tests-calls">
              <span className="field-label">
                Outside calls · {r.calls.length}
                {r.moreCalls ? ` (and ${r.moreCalls} more)` : ''}
              </span>
              <ol>
                {r.calls.map((call, i) => (
                  <li key={i}>
                    <details>
                      <summary>
                        <span className="mono">
                          {call.service} {call.method} {call.path}
                        </span>{' '}
                        <Chip small tone={call.error || (call.status ?? 0) >= 400 ? 'crit' : 'neutral'}>
                          {call.error ? 'no answer' : String(call.status)}
                        </Chip>{' '}
                        <span className="muted">{call.ms} ms</span>
                      </summary>
                      <pre className="sip-text">
                        {JSON.stringify({ ...(call.request !== undefined ? { sent: call.request } : {}), ...(call.response !== undefined ? { answered: call.response } : {}), ...(call.error ? { error: call.error } : {}) }, null, 2)}
                      </pre>
                    </details>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function HandsOn({ cases, run, onSaved }: { cases: TestCaseInfo[]; run: TestRunView; onSaved: (r: TestRunView, message: string) => void }) {
  return (
    <Card title="Hands-on checks">
      <p className="card-body muted tests-intro">
        Things only a person can check: a phone ringing, a text arriving, a TV showing the wallboard. Try each one on the test tenant and mark what happened. Notes go in the download with phone
        numbers, emails and names taken out.
      </p>
      {byArea(cases).map(([area, list]) => (
        <section key={area} className="tests-area">
          <h3 className="tests-area-title">{area}</h3>
          <ul className="tests-list">
            {list.map((c) => (
              <HandsRow key={c.id} c={c} run={run} onSaved={onSaved} />
            ))}
          </ul>
        </section>
      ))}
    </Card>
  );
}

function HandsRow({ c, run, onSaved }: { c: TestCaseInfo; run: TestRunView; onSaved: (r: TestRunView, message: string) => void }) {
  const mark = run.manual[c.id];
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(mark?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => setNote(run.manual[c.id]?.note ?? ''), [run.id, c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (status: HandsOnMark['status'] | null) => {
    setBusy(true);
    setError('');
    try {
      const r = (await api.markHandsOn(run.id, c.id, status ? { status, note } : null)).run;
      onSaved(r, status ? `“${c.title}” marked ${HANDS_STATUS[status].label.toLowerCase()}.` : `“${c.title}” cleared.`);
      if (status) setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const s = mark ? HANDS_STATUS[mark.status] : null;
  return (
    <li className="tests-row" data-case={c.id}>
      <div className="tests-row-head">
        <Chip small tone={s?.tone ?? 'neutral'}>
          {s?.label ?? 'Not done'}
        </Chip>
        <span className="cell-stack grow">
          <span className="tests-title">{c.title}</span>
          {mark ? (
            <small className="muted">
              {mark.by} · {shortWhen(mark.at)}
              {mark.note ? ` · ${mark.note}` : ''}
            </small>
          ) : (
            <small className="muted">{c.expect}</small>
          )}
        </span>
        <button type="button" className="link-btn small-print" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? 'Close' : mark ? 'Change' : 'Try it'}
        </button>
      </div>
      {open && (
        <div className="tests-more">
          {c.steps && (
            <ol className="tests-steps">
              {c.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          )}
          <p className="small-print">
            <strong>Should:</strong> {c.expect}
          </p>
          {c.caution && (
            <Notice tone="warn" icon="alert">
              {c.caution}
            </Notice>
          )}
          <div className="field stretch">
            <label htmlFor={`note-${c.id}`}>What happened (optional)</label>
            <textarea id={`note-${c.id}`} className="input textarea" rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="wizard-actions">
            <Button size="small" icon="check" disabled={busy} onClick={() => void save('pass')}>
              Worked
            </Button>
            <Button size="small" variant="danger" icon="x" disabled={busy} onClick={() => void save('fail')}>
              Didn’t work
            </Button>
            <Button size="small" variant="ghost" disabled={busy} onClick={() => void save('blocked')}>
              Couldn’t try
            </Button>
            {mark && (
              <Button size="small" variant="ghost" disabled={busy} onClick={() => void save(null)}>
                Clear
              </Button>
            )}
          </div>
          {error && <Notice tone="crit">{error}</Notice>}
        </div>
      )}
    </li>
  );
}

function PastRuns({ v, current, onOpen }: { v: TestCenterView; current: string | null; onOpen: (id: string) => void }) {
  if (v.runs.length === 0) return null;
  return (
    <Card title="Past runs">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Started</th>
              <th scope="col">By</th>
              <th scope="col">What</th>
              <th scope="col">State</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {v.runs.map((r) => {
              const state = r.finishedAt ? 'Finished' : v.running === r.id ? 'Running' : 'Stopped';
              return (
                <tr key={r.id} className={r.id === current ? 'selected' : undefined}>
                  <td>{shortWhen(r.startedAt)}</td>
                  <td>{r.startedByName}</td>
                  <td>{r.options.writes ? 'With changes' : 'Reads only'}</td>
                  <td>
                    <Chip small tone={state === 'Finished' ? 'ok' : state === 'Running' ? 'brand' : 'warn'}>
                      {state}
                    </Chip>
                  </td>
                  <td className="row-actions">
                    {r.id === current ? (
                      <span className="muted small-print">Showing</span>
                    ) : (
                      <Button size="small" variant="ghost" onClick={() => onOpen(r.id)}>
                        Open
                      </Button>
                    )}
                    <a className="btn ghost small" href={api.testReportUrl(r.id)} download aria-label={`Download the run from ${shortWhen(r.startedAt)}`}>
                      <Icon name="download" size={15} strokeWidth={2.2} />
                      JSON
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
